<template>
  <el-form-item label="提交标题" :class="{ 'is-error': !validationState.name }" class="mobile-form-item">
    <div class="title-input-container relative">
      <div class="flex gap-2">
        <el-input 
          v-model="form.name" 
          placeholder="输入标题,如果不知道写什么标题,可以试试推荐哦~" 
          class="mobile-input flex-1" 
          maxlength="40"
          show-word-limit
          type="text"
        />
        <el-button 
          type="primary" 
          @click="toggleRecommendations"
          class="recommend-btn"
        >
          <el-icon class="mr-1"><Star /></el-icon>
          推荐标题
        </el-button>
      </div>
      
      <!-- 推荐标题悬浮框 -->
      <div 
        v-if="showRecommendations"
        class="recommendations-dropdown absolute top-full left-0 right-0 bg-white border border-[#e1e5e9] rounded-lg shadow-2xl z-50 mt-2"
      >
        <!-- 热门标题标签 -->
        <div class="hot-topic-header">
          <div class="flex items-center gap-2 mb-3">
            <div class="w-2 h-2 bg-gradient-to-r from-[#ff6b6b] to-[#ee5a24] rounded-full animate-pulse"></div>
            <span class="text-sm font-semibold text-[#2d3748]">热门标题</span>
          </div>
          
          <!-- 分类标签 -->
          <div class="category-tabs-container">
            <div class="category-scroll-wrapper relative">
              <div 
                ref="categoryScrollRef"
                class="category-scroll flex overflow-x-auto gap-2 pb-2"
                @scroll="handleScroll"
              >
                <button
                  v-for="category in availableCategories"
                  :key="category"
                  @click.prevent="selectCategory(category)"
                  type="button"
                  :class="{
                    'bg-gradient-to-r from-[#4299e1] to-[#3182ce] text-white shadow-md border-[#3182ce]': selectedCategory === category,
                    'bg-white text-[#4a5568] border-[#cbd5e0] hover:bg-[#f7fafc] hover:border-[#4299e1]': selectedCategory !== category
                  }"
                  class="px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-200 whitespace-nowrap flex-shrink-0"
                >
                  {{ category }}
                </button>
              </div>
              <!-- PC端滑动按钮 -->
              <button
                v-if="showScrollButton"
                @click.prevent="scrollLeft"
                type="button"
                class="scroll-btn scroll-btn-left"
                :class="{ 'opacity-50 cursor-not-allowed': !canScrollLeft }"
              >
                <el-icon><ArrowLeft /></el-icon>
              </button>
              <button
                v-if="showScrollButton"
                @click.prevent="scrollRight"
                type="button"
                class="scroll-btn scroll-btn-right"
                :class="{ 'opacity-50 cursor-not-allowed': !canScrollRight }"
              >
                <el-icon><ArrowRight /></el-icon>
              </button>
            </div>
          </div>
        </div>

        <!-- 推荐标题列表 -->
        <div class="suggestions-grid">
          <div 
            v-for="(suggestion, index) in currentSuggestions" 
            :key="index"
            @click="selectSuggestion(suggestion)"
            class="suggestion-card cursor-pointer hover:shadow-md transition-all duration-200"
          >
            <div class="suggestion-title">{{ suggestion.title }}</div>
          </div>
        </div>

        <!-- 换一批按钮 -->
        <div class="text-center mt-4 pb-4">
          <el-button 
            @click="refreshSuggestions"
            class="refresh-btn"
            :loading="isRefreshing"
          >
            <el-icon class="mr-1"><Refresh /></el-icon>
            换一批
          </el-button>
        </div>
      </div>
    </div>
  </el-form-item>
  <el-form-item label="类型" :class="{ 'is-error': !validationState.region }" class="mobile-form-item">
    <el-select v-model="form.region" placeholder="请选择" class="mobile-select w-full">
      <el-option label="国家开放大学毕业论文" value="0" />
    </el-select>
  </el-form-item>
  <el-form-item label="关键词" :class="{ 'is-error': !validationState.keywords }" class="mobile-form-item">
    <div class="keywords-container relative">
      <div class="flex gap-2">
        <InputTag v-model="form.keywords" :is-invalid="!validationState.keywords" class="flex-1" />
        <el-button 
          @click="generateKeywords"
          :loading="isGeneratingKeywords"
          :disabled="!form.name.trim()"
          class="ai-generate-btn"
          type="primary"
        >
          <el-icon class="mr-1">
            <component :is="form.keywords && form.keywords.length > 0 ? Refresh : Lightning" />
          </el-icon>
          {{ form.keywords && form.keywords.length > 0 ? '重新生成' : 'AI生成' }}
        </el-button>
      </div>
      
      <!-- AI生成关键词加载提示 -->
      <div v-if="isGeneratingKeywords" class="ai-loading-overlay">
        <div class="ai-loading-content">
          <div class="ai-loading-spinner"></div>
          <span class="ai-loading-text">AI正在为您生成关键词...</span>
        </div>
      </div>
    </div>
  </el-form-item>
  <el-form-item label="字数预估" class="mobile-form-item">
    <el-select v-model="form.wordNumber" placeholder="请选择" class="mobile-select w-full">
      <el-option label="6000字版" :value="6000" />
      <el-option label="10000字版" :value="10000" />
      <el-option label="20000字版" :value="20000" />
    </el-select>
  </el-form-item>

</template>

<script setup lang="ts">
import { toRefs, ref, computed, watch } from 'vue';
import { defineProps } from 'vue';
import { Star, Refresh, ArrowRight, Lightning, ArrowLeft } from '@element-plus/icons-vue';
import InputTag from '@/components/inputTag.vue';
import { hotTopicSuggestions, type SuggestionItem } from '@/data/hotTopicSuggestions';
import { aiGenerateKeywords } from '@/api/api';

interface Props {
  form: {
    name: string;
    region: string;
    keywords: string[];
    wordNumber: number;
  };
  validationState: {
    name: boolean;
    region: boolean;
    keywords: boolean;
  }
}

const props = defineProps<Props>();
const { form, validationState } = toRefs(props);

// 推荐标题悬浮框功能
const showRecommendations = ref(false);
const selectedCategory = ref('');
const isRefreshing = ref(false);
const currentBatchIndex = ref(0);

// 分类标签滑动功能
const categoryScrollRef = ref<HTMLElement>();
const canScrollLeft = ref(false);
const canScrollRight = ref(false);
const showScrollButton = ref(false);

// AI生成关键词功能
const isGeneratingKeywords = ref(false);

// 获取所有可用的分类
const availableCategories = computed(() => {
  return Object.keys(hotTopicSuggestions);
});


// 初始化默认分类
const initializeDefaultCategory = () => {
  if (!selectedCategory.value) {
    // 默认选择第一个分类
    const firstCategory = availableCategories.value[0];
    if (firstCategory) {
      selectedCategory.value = firstCategory;
    }
  }
};

// 切换推荐标题显示
const toggleRecommendations = () => {
  if (!showRecommendations.value) {
    initializeDefaultCategory();
    // 延迟检查是否需要显示滑动按钮
    setTimeout(() => {
      checkScrollButton();
    }, 100);
  }
  showRecommendations.value = !showRecommendations.value;
};

// 检查是否需要显示滑动按钮
const checkScrollButton = () => {
  if (!categoryScrollRef.value) return;
  
  const scrollElement = categoryScrollRef.value;
  const isOverflowing = scrollElement.scrollWidth > scrollElement.clientWidth;
  
  showScrollButton.value = isOverflowing;
  canScrollLeft.value = scrollElement.scrollLeft > 0;
  canScrollRight.value = scrollElement.scrollLeft < (scrollElement.scrollWidth - scrollElement.clientWidth);
};

// 处理滚动事件
const handleScroll = () => {
  if (!categoryScrollRef.value) return;
  
  const scrollElement = categoryScrollRef.value;
  canScrollLeft.value = scrollElement.scrollLeft > 0;
  canScrollRight.value = scrollElement.scrollLeft < (scrollElement.scrollWidth - scrollElement.clientWidth);
};

// 向左滑动
const scrollLeft = () => {
  if (!categoryScrollRef.value || !canScrollLeft.value) return;
  
  const scrollElement = categoryScrollRef.value;
  const scrollAmount = 200; // 每次滑动的距离
  
  scrollElement.scrollTo({
    left: scrollElement.scrollLeft - scrollAmount,
    behavior: 'smooth'
  });
};

// 向右滑动
const scrollRight = () => {
  if (!categoryScrollRef.value || !canScrollRight.value) return;
  
  const scrollElement = categoryScrollRef.value;
  const scrollAmount = 200; // 每次滑动的距离
  const maxScroll = scrollElement.scrollWidth - scrollElement.clientWidth;
  
  scrollElement.scrollTo({
    left: Math.min(scrollElement.scrollLeft + scrollAmount, maxScroll),
    behavior: 'smooth'
  });
};

// 根据选择的分类获取推荐标题
const currentSuggestions = computed(() => {
  const category = selectedCategory.value;
  let allSuggestions: SuggestionItem[] = [];
  
  if (category && hotTopicSuggestions[category]) {
    allSuggestions = hotTopicSuggestions[category];
  } else {
    allSuggestions = [
      { title: '数字化转型对现代企业管理的影响研究', category: '通用' },
      { title: '人工智能技术在各行业的应用前景', category: '通用' },
      { title: '可持续发展理念在专业实践中的体现', category: '通用' },
      { title: '创新驱动发展战略的实施路径研究', category: '通用' },
      { title: '新时代背景下的行业发展趋势分析', category: '通用' },
      { title: '数字经济时代的企业转型策略研究', category: '通用' },
      { title: '绿色发展与生态文明建设路径探索', category: '通用' },
      { title: '科技创新驱动产业升级机制研究', category: '通用' }
    ];
  }
  
  // 每批显示4个标题
  const batchSize = 4;
  const startIndex = currentBatchIndex.value * batchSize;
  const endIndex = startIndex + batchSize;
  
  return allSuggestions.slice(startIndex, endIndex);
});


// 换一批功能
const refreshSuggestions = async () => {
  isRefreshing.value = true;
  
  // 模拟刷新延迟
  setTimeout(() => {
    const category = selectedCategory.value;
    let allSuggestions: SuggestionItem[] = [];
    
    if (category && hotTopicSuggestions[category]) {
      allSuggestions = hotTopicSuggestions[category];
    } else {
      allSuggestions = [
        { title: '数字化转型对现代企业管理的影响研究', category: '通用' },
        { title: '人工智能技术在各行业的应用前景', category: '通用' },
        { title: '可持续发展理念在专业实践中的体现', category: '通用' },
        { title: '创新驱动发展战略的实施路径研究', category: '通用' },
        { title: '新时代背景下的行业发展趋势分析', category: '通用' },
        { title: '数字经济时代的企业转型策略研究', category: '通用' },
        { title: '绿色发展与生态文明建设路径探索', category: '通用' },
        { title: '科技创新驱动产业升级机制研究', category: '通用' }
      ];
    }
    
    // 计算下一批的索引，如果超出范围则回到第一批
    const batchSize = 4;
    const maxBatches = Math.ceil(allSuggestions.length / batchSize);
    currentBatchIndex.value = (currentBatchIndex.value + 1) % maxBatches;
    
    isRefreshing.value = false;
  }, 500);
};

const selectCategory = (category: string) => {
  selectedCategory.value = category;
  currentBatchIndex.value = 0; // 切换分类时重置到第一批
};

const selectSuggestion = (suggestion: SuggestionItem) => {
  form.value.name = suggestion.title;
  showRecommendations.value = false;
};

// AI生成关键词函数
const generateKeywords = async () => {
  if (isGeneratingKeywords.value) return; // 防止重复请求
  
  try {
    isGeneratingKeywords.value = true;
    const response = await aiGenerateKeywords(form.value.name.trim());
    
    // 检查响应是否成功
    if (response.data && response.data.code === 0 && Array.isArray(response.data.data)) {
      // 自动填入关键词
      form.value.keywords = response.data.data;
    } else {
      console.warn('AI生成关键词返回格式异常:', response.data);
      // 可以根据具体的错误码显示不同的提示信息
      if (response.data && response.data.msg) {
        console.warn('错误信息:', response.data.msg);
      }
    }
  } catch (error) {
    console.error('AI生成关键词失败:', error);
    // 可以在这里添加用户提示，比如显示一个错误消息
  } finally {
    isGeneratingKeywords.value = false;
  }
};

// 监听标题变化，自动生成关键词
watch(() => form.value.name, async (newTitle) => {
  if (newTitle && newTitle.trim() && (!form.value.keywords || form.value.keywords.length === 0)) {
    await generateKeywords();
  }
});

</script>

<style scoped>
.mobile-form-item {
  margin-bottom: 24px;
}

.mobile-form-item :deep(.el-form-item__label) {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  width: 100px;
  text-align: left;
  line-height: 32px;
  margin-right: 12px;
  flex-shrink: 0;
}

.mobile-form-item :deep(.el-form-item__content) {
  flex: 1;
  margin-left: 0 !important;
}

.mobile-input {
  width: 100%;
}

.mobile-input :deep(.el-input__wrapper) {
  width: 100%;
  height: 40px;
  box-shadow: 0 0 0 1px #dcdfe6 inset;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.mobile-input :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #c0c4cc inset;
}

.mobile-input :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #409eff inset;
}

.mobile-select {
  width: 100%;
}

.mobile-select :deep(.el-input__wrapper) {
  width: 100%;
  height: 40px;
  box-shadow: 0 0 0 1px #dcdfe6 inset;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.mobile-select :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #c0c4cc inset;
}

.mobile-select :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #409eff inset;
}

.title-input-container {
  position: relative;
  width: 100%;
}

/* 推荐按钮样式 */
.recommend-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  font-weight: 500;
  padding: 0 16px;
  height: 32px;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.recommend-btn:hover {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 推荐标题悬浮框样式 */
.recommendations-dropdown {
  animation: slideDownFade 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  max-height: 400px;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
}

@keyframes slideDownFade {
  from {
    opacity: 0;
    transform: translateY(-12px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 热门标题头部 */
.hot-topic-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 12px;
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid #e2e8f0;
}

/* 分类标签容器 */
.category-tabs-container {
  margin-top: 12px;
}

/* 分类标签滚动容器 */
.category-scroll-wrapper {
  position: relative;
}

.category-scroll {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  padding-right: 50px; /* 为滑动按钮留出更多空间 */
}

.category-scroll::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

/* 滑动按钮样式 */
.scroll-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.scroll-btn:hover {
  background: rgba(255, 255, 255, 1);
  border-color: #4299e1;
  box-shadow: 0 4px 12px rgba(66, 153, 225, 0.2);
}

.scroll-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.scroll-btn:disabled:hover {
  background: rgba(255, 255, 255, 0.9);
  border-color: #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.scroll-btn-right {
  right: 8px;
}

.scroll-btn-left {
  left: 8px;
}

/* PC端显示滑动按钮，移动端隐藏 */
@media (min-width: 769px) {
  .scroll-btn {
    display: flex;
  }
  
  .category-scroll {
    padding-left: 50px;
    padding-right: 50px; /* 为滑动按钮留出空间 */
  }
}

@media (max-width: 768px) {
  .scroll-btn {
    display: none;
  }
  
  .category-scroll {
    padding-right: 0;
  }
}

/* 滑动按钮图标样式 */
.scroll-btn .el-icon {
  font-size: 14px;
  color: #4a5568;
}

.scroll-btn:hover .el-icon {
  color: #4299e1;
}

.scroll-btn:disabled .el-icon {
  color: #a0aec0;
}

/* 推荐标题网格 */
.suggestions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  padding: 12px;
}

/* 推荐标题卡片 */
.suggestion-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 12px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.suggestion-card:hover {
  border-color: #4299e1;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(66, 153, 225, 0.15);
}

.suggestion-card:hover::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #4299e1, #3182ce);
}

.suggestion-title {
  font-size: 13px;
  font-weight: 500;
  color: #2d3748;
  line-height: 1.4;
  word-break: break-word;
}

.suggestion-card:hover .suggestion-title {
  color: #2b6cb0;
}

/* 换一批按钮 */
.refresh-btn {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  border: none;
  color: white;
  font-weight: 500;
  padding: 8px 20px;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.refresh-btn:hover {
  background: linear-gradient(135deg, #38a169 0%, #2f855a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.4);
}

/* 悬浮框滚动条样式 */
.recommendations-dropdown::-webkit-scrollbar {
  width: 4px;
}

.recommendations-dropdown::-webkit-scrollbar-track {
  background: rgba(237, 242, 247, 0.3);
}

.recommendations-dropdown::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #cbd5e0, #a0aec0);
  border-radius: 2px;
}

.recommendations-dropdown::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #a0aec0, #718096);
}


/* 移动端适配 */
@media (max-width: 768px) {
  .mobile-form-item {
    margin-bottom: 20px;
  }
  
  .mobile-form-item :deep(.el-form-item__label) {
    font-size: 13px;
    width: 80px;
    margin-right: 10px;
    line-height: 28px;
  }
  
  .mobile-input :deep(.el-input__wrapper),
  .mobile-select :deep(.el-input__wrapper) {
    border-radius: 6px;
  }
  
  /* 推荐悬浮框移动端适配 */
  .recommendations-dropdown {
    max-height: 350px;
    left: -4px;
    right: -4px;
  }
  
  .suggestions-grid {
    grid-template-columns: 1fr;
    gap: 6px;
    padding: 8px;
  }
  
  .suggestion-card {
    padding: 10px;
  }
  
  .suggestion-title {
    font-size: 12px;
  }
  
  .recommend-btn {
    padding: 0 12px;
    font-size: 12px;
  }
  
  .hot-topic-header {
    padding: 10px;
  }
  
  .ai-generate-btn {
    padding: 0 12px;
    font-size: 12px;
  }
}

/* AI生成关键词加载样式 */
.keywords-container {
  position: relative;
}

/* AI生成按钮样式 */
.ai-generate-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  font-weight: 500;
  padding: 0 16px;
  min-height: 40px;
  height: auto;
  border-radius: 6px;
  transition: all 0.3s ease;
  white-space: nowrap;
  align-self: flex-start;
}

.ai-generate-btn:hover {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.ai-generate-btn:disabled {
  background: #cbd5e0;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.ai-generate-btn:disabled:hover {
  background: #cbd5e0;
  transform: none;
  box-shadow: none;
}

.ai-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(2px);
  border-radius: 4px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-loading-content {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(64, 158, 255, 0.1);
  border: 1px solid rgba(64, 158, 255, 0.2);
  border-radius: 6px;
  font-size: 12px;
  color: #409eff;
}

.ai-loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(64, 158, 255, 0.2);
  border-left: 2px solid #409eff;
  border-radius: 50%;
  animation: ai-spin 1s linear infinite;
}

.ai-loading-text {
  font-weight: 500;
  white-space: nowrap;
}

@keyframes ai-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 响应式设计增强 */
@media (max-width: 480px) {
  .mobile-form-item :deep(.el-form-item__label) {
    font-size: 12px;
    width: 70px;
    margin-right: 8px;
    line-height: 26px;
  }
  
  .recommendations-dropdown {
    left: -8px;
    right: -8px;
    max-height: 300px;
  }
  
  .suggestions-grid {
    padding: 6px;
  }
  
  .suggestion-card {
    padding: 8px;
  }
  
  .suggestion-title {
    font-size: 11px;
  }
  
  .recommend-btn {
    padding: 0 8px;
    font-size: 11px;
  }
  
  .ai-generate-btn {
    padding: 0 8px;
    font-size: 11px;
  }
  
  .hot-topic-header {
    padding: 8px;
  }
  
  .ai-loading-content {
    font-size: 11px;
    padding: 6px 12px;
  }
  
  .ai-loading-spinner {
    width: 14px;
    height: 14px;
  }
}
</style>
